import { useSelector } from 'react-redux';
import { merge, get } from 'lodash';
import CryptoJS from 'crypto-js';
import appConfig from 'Config';
import { usePageContext } from 'Context/PageContext';
import { useAppContext } from 'Context/ReactAppContext';
import { getLeadScore } from 'Store/lead-info/leadInfo.selector';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import {
  STATIC_PAGE_TYPES,
  TRACK_EVENT_NAMES,
  TRACK_SITE_SECTIONS,
  BE_TRACKING,
} from 'Tracking/constants';
import { getConnectionStatus, getNeolaneId, getPageSiteSection } from 'Tracking/utils';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { getCurrencyCode } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { useTrackEvent } from './useTrackEvent';
export const useTrackPage = () => {
  const appContext = useAppContext();
  const { pageType } = usePageContext();
  const {
    countryCode,
    countryName,
    currency,
    customerStatus,
    googleTrackingId,
    language,
    locale,
    isSeller,
  } = appContext;
  const profile: UserInfo = useSelector(userInfoSelector);
  const leadScoring = useSelector(getLeadScore);
  const { trackEvent } = useTrackEvent();
  const site_section = getPageSiteSection({
    pageType,
  });
  const shouldUseB2BUser = isSeller && pageType === STATIC_PAGE_TYPES.BE;
  const b2bUser = {
    locale: appContext.locale,
    currency: getCurrencyCode(appContext.locale),
    environment: appConfig.get('tracking.environment'),
    country_code: appContext.countryCode,
    agent: appContext.isCmta ? appContext.cmta.salesmanID : appContext.cxoQueryParams?.clubmed_id,
    agence: appContext.cxoQueryParams.agency_id,
    contract_type: 'non_ducroire',
    seller_type: appContext.isCmta ? 'TA' : 'TED',
  };
  // Get email_sha_256 from tracking storage (set when participant form is submitted)
  const storedTracking = trackingStorageApi.get(BE_TRACKING) as Record<string, unknown> | null;
  const storedEmailSha256 = get(storedTracking, 'email_sha_256') as string | undefined;
  const params = {
    site_section,
    user: shouldUseB2BUser
      ? b2bUser
      : {
          be_customer_status: customerStatus || 'not_logged',
          connection_status: getConnectionStatus(appContext),
          country_code: countryCode,
          country_language: language,
          country_name: countryName,
          currency,
          environment: appConfig.get('tracking.environment'),
          fidelity_status: profile?.loyaltyProgram?.status,
          full_visitor_id: googleTrackingId,
          lead_scoring_range: leadScoring,
          locale: locale,
          neolane_id: getNeolaneId(appContext),
          visitor_guid: googleTrackingId,
          ...(storedEmailSha256 &&
            site_section === TRACK_SITE_SECTIONS.BE && {
              email_sha_256: storedEmailSha256,
            }),
          ...(profile?.email &&
            [TRACK_SITE_SECTIONS.CA, TRACK_SITE_SECTIONS.BE].includes(site_section) && {
              email_sha_256: CryptoJS.SHA256(profile.email).toString(), // eslint-disable-line new-cap
            }),
        },
  } as TrackPayload;

  const trackPage = (pageName: string, payload?: TrackPayload) => {
    trackEvent(
      TRACK_EVENT_NAMES.PAGE_VIEW,
      merge(
        {
          page_name: pageName,
        },
        payload,
        params,
      ),
    );
  };

  return {
    trackPage,
  };
};
